import { FontAwesome5 } from '@expo/vector-icons';
import { secondaryFontFamily, styled } from '@openeducation/oe-rn-themes';
import React, { PropsWithChildren } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';

import { OETouchableOpacity, OETouchableOpacityProps } from '../../atoms';
import { OEText } from '../../atoms/oe-text/oe-text';
import { flex, opacity, padding } from '../../style-utils';

type PaginationButtonType = {
  onPress: () => void;
  disabled?: boolean;
  text?: string;
};

export type PaginationButtonsProps = {
  prev?: PaginationButtonType;
  next?: PaginationButtonType;
  color?: string;
  size?: number;
  iconType?: 'arrow' | 'chevron';
  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
};

export const PaginationButtons = ({
  prev,
  next,
  color,
  size = 12,
  iconType = 'chevron',
  style,
  children,
}: PaginationButtonsProps) => {
  return (
    <View
      style={[
        flex('flex-row', 'justify-content-center', 'align-items-center'),
        style,
      ]}
    >
      <View style={[flex('align-items-end')]}>
        {!!prev && (
          <_OETouchableOpacity
            onPress={prev.onPress}
            size={size}
            disabled={prev.disabled}
          >
            <ArrowIcon name={`${iconType}-left`} color={color} size={size} />
            {!!prev.text && (
              <ButtonText color={color} size={size}>
                {prev.text}
              </ButtonText>
            )}
          </_OETouchableOpacity>
        )}
      </View>
      {children}
      <View style={[flex('align-items-start')]}>
        {!!next && (
          <_OETouchableOpacity
            onPress={next.onPress}
            size={size}
            disabled={next.disabled}
          >
            {!!next.text && (
              <ButtonText color={color} size={size}>
                {next.text}
              </ButtonText>
            )}
            <ArrowIcon name={`${iconType}-right`} color={color} size={size} />
          </_OETouchableOpacity>
        )}
      </View>
    </View>
  );
};

const _OETouchableOpacity = ({
  onPress,
  size,
  children,
  disabled,
  ...props
}: PropsWithChildren<{ size: number }> & OETouchableOpacityProps) => (
  <OETouchableOpacity
    onPress={onPress}
    disabled={disabled}
    style={[
      flex('flex-row', 'align-items-center'),
      padding(15),
      opacity(disabled ? 0.75 : 1),
    ]}
    hitSlop={{
      top: size + 8,
      right: size - 4,
      bottom: size + 8,
      left: size - 4,
    }}
    {...props}
  >
    {children}
  </OETouchableOpacity>
);

const ButtonText = styled(OEText).attrs<{ size: number }>(
  ({ theme, color, size }) => ({
    color: color || theme.color.primary,
    size: size + 2,
    fontFamily: secondaryFontFamily,
    fontType: 'medium',
  })
)<{ size: number }>`
  padding-bottom: 1px;
`;

const ArrowIcon = styled(FontAwesome5).attrs<{ size: number }>(
  ({ theme, color, size }) => ({
    color: color || theme.color.primary,
    size,
  })
)`
  padding-horizontal: 4px;
`;
